import React, { useMemo, useState } from 'react';
import styles from './TiktokCollectionsTracker.module.css';
import { Helmet } from 'react-helmet';
import MetricCard from '../../../../../../ui/DataDisplay/Statistics/MetricCard/MetricCard';
import TiktokCollectionsAudios from '../TiktokCollectionsAudios/TiktokCollectionsAudios';
import TiktokCollectionsTopPosts from '../TIktokCollectionsTopPosts/TiktokCollectionsTopPosts';
import MusicNoteIcon from '../../../../../../SharedComponents/svg/Icons/MusicNoteIcon';
import VideoCameraIcon from '../../../../../../SharedComponents/svg/Icons/VideoCameraIcon';
import CalendarMonthIcon from '../../../../../../SharedComponents/svg/Icons/CalendarMonthIcon';
import useNonNullContext from '../../../../../../Hooks/useNonNullContext';
import { CollectionsContext } from '../contexts/CollectionsContext';
import useCollectionAudios from '../contexts/useCollectionsAudios';
import useCollectionTopPosts from '../contexts/useCollectionTopPosts';
import Card from '../../../../../../ui/Card/Card';
import SelectBox from '../../../../../../ui/DataEntry/SelectBox/SelectBox';
import { formatAudioCount } from '../helpers';
import { TiktokAudioCollection } from '@round/api';
import { ProtectedByUserGroups } from '../../../../../Auth/components/ProtectedByUserGroup/ProtectedByUserGroups';
import Button from '../../../../../../ui/Buttons/Button/Button';
import { ReactComponent as AddIcon } from '../../../../../../assets/Add.svg';
import CreateCollectionModal from '../CreateCollectionModal/CreateCollectionModal';
import { GenericDropdownOption } from '../../../../../../App.types';
import CollectionOption from '../components/CollectionOption/CollectionOption';
import EditCollectionModal from '../EditCollectionModal/EditCollectionModal';
import { OptionsContext } from '../../../../../../contexts/Options';

const mapCollectionToOption = (collection: TiktokAudioCollection) => ({
    id: collection.id,
    name: collection.name,
    subtitle: formatAudioCount(collection.tiktok_audio_count),
    picture: collection.picture ?? '',
});

const TiktokCollectionsTracker = () => {
    const [
        {
            urlState,
            selectedCollection,
            collections,
            collectionsLoading,
            headlineStatsLoading,
            headlineStats,
            errorLoadingCollections,
            collectionsCount,
        },
        { setSelectedCollection, loadMoreCollections, setUrlState, addCollection, updateCollection },
    ] = useNonNullContext(CollectionsContext);

    const collectionOptions = collections.map(mapCollectionToOption);

    const [audiosValues, { refresh: refreshAudios, ...audiosUtils }] = useCollectionAudios();
    const [topPostsValues, { refresh: refreshTopPosts, ...topPostsUtils }] = useCollectionTopPosts();
    const [createCollectionModalOpen, setCreateCollectionModalOpen] = useState(false);
    const [collectionToEdit, setCollectionToEdit] = useState<TiktokAudioCollection | null>(null);

    const { clients } = useNonNullContext(OptionsContext);
    const clientOptions: GenericDropdownOption<number>[] = clients.map((client) => ({
        value: client.id,
        label: client.name,
    }));

    const title = useMemo(() => {
        let title = 'Collections | trnds';

        if (selectedCollection) {
            title = `${selectedCollection.name} | ${title}`;
        }

        return title;
    }, [selectedCollection]);

    return (
        <>
            <Helmet>
                <title>{title}</title>
            </Helmet>

            <div className={styles.container}>
                <div className={styles.header}>
                    <div className={styles.collectionSelectContainer}>
                        <SelectBox
                            searchable
                            search={urlState.search}
                            setSearch={(search) => setUrlState({ search })}
                            loading={collectionsLoading && !selectedCollection}
                            value={selectedCollection ? mapCollectionToOption(selectedCollection) : undefined}
                            onChange={(option) => setSelectedCollection(collections.find((c) => c.id === option.id))}
                            options={collectionOptions}
                            optionsCount={collectionsCount}
                            optionsLoading={collectionsLoading}
                            loadMore={loadMoreCollections}
                            placeholder="Select collection"
                            noOptionsLabel={
                                errorLoadingCollections ? 'Sorry, we are experiencing technical issues' : 'No Options'
                            }
                            renderDropdownBottom={(setShowDropdown) => (
                                <ProtectedByUserGroups groups={['catalogue_editor']}>
                                    <Button
                                        className={styles.createCollectionButton}
                                        contentClassName={styles.createCollectionButtonContents}
                                        onClick={() => {
                                            setCreateCollectionModalOpen(true);
                                            setShowDropdown(false);
                                        }}
                                    >
                                        <AddIcon className={styles.createCollectionIcon} /> Create new collection
                                    </Button>
                                </ProtectedByUserGroups>
                            )}
                            renderItem={(props) => (
                                <CollectionOption
                                    item={props.item}
                                    loading={props.loading}
                                    onEdit={(item) =>
                                        setCollectionToEdit(collections.find((c) => c.id === item.id) ?? null)
                                    }
                                    onClick={props.onClick}
                                    style={props.style}
                                    setShowDropdown={props.setShowDropdown}
                                />
                            )}
                        />
                    </div>
                </div>

                <Card className={styles.content}>
                    <div className={styles.headlineStatsContainer}>
                        <MetricCard
                            baseColor="#0090FF"
                            loading={headlineStatsLoading}
                            value={headlineStats?.audio_count}
                            label="Total Audios Tracked"
                            Icon={MusicNoteIcon}
                        />
                        <MetricCard
                            baseColor="#F7685B"
                            loading={headlineStatsLoading}
                            value={headlineStats?.total_video_count}
                            label="Total Video Creations"
                            Icon={VideoCameraIcon}
                        />
                        <MetricCard
                            baseColor="#FFA114"
                            loading={headlineStatsLoading}
                            value={headlineStats?.new_video_count_7_days}
                            label="creations in the last 7 days"
                            Icon={CalendarMonthIcon}
                        />
                        <MetricCard
                            baseColor="#48D98A"
                            loading={headlineStatsLoading}
                            value={headlineStats?.new_video_count_1_day}
                            label="Daily Creations"
                            Icon={CalendarMonthIcon}
                        />
                    </div>

                    <div data-test-id="audios-table-container">
                        <TiktokCollectionsAudios {...audiosValues} {...audiosUtils} />
                    </div>

                    <div data-test-id="top-posts-table-container">
                        <TiktokCollectionsTopPosts {...topPostsValues} {...topPostsUtils} />
                    </div>
                </Card>
            </div>

            <ProtectedByUserGroups groups={['catalogue_editor']}>
                <CreateCollectionModal
                    isModalOpen={createCollectionModalOpen}
                    closeModal={() => setCreateCollectionModalOpen(false)}
                    clientOptions={clientOptions}
                    onCreated={addCollection}
                />
            </ProtectedByUserGroups>

            {collectionToEdit && (
                <ProtectedByUserGroups groups={['catalogue_editor']}>
                    <EditCollectionModal
                        isModalOpen={Boolean(collectionToEdit)}
                        closeModal={() => setCollectionToEdit(null)}
                        collection={collectionToEdit}
                        clientOptions={clientOptions}
                        onUpdated={updateCollection}
                    />
                </ProtectedByUserGroups>
            )}
        </>
    );
};

export default TiktokCollectionsTracker;
